import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Wrench, ShieldCheck, DollarSign, CircleDollarSign, TrendingDown, ArrowUpRight,
  FileWarning, Cpu, ChevronRight, Bell, Battery, AlertCircle,
  Calendar, Check, X as XIcon, Trash2, Sparkles, Sprout, Grid3X3,
} from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";
import { getClimate } from "../../../lib/climateData";
import {
  financialSnapshot,
  serviceTimeline,
  warrantyRisk,
  capacityRetention,
  eviqScore,
  batteryRiskScore,
  futureHealthForecast,
  resaleBatteryValue,
  thermalStressIndex,
  chargingQualityScore,
  calibrationConfidence,
} from "../../../lib/batteryAnalytics";
import {
  PillarSectionHeader,
  StatChipCard,
  MeterBarCard,
  FactorListCard,
  COLOR,
} from "../pillars/_cards";
import { MoreView } from "./MoreView";
import { CollapseSection } from "../CollapseSection";
import { serviceLabel, warrantyFor } from "../../../lib/brand";
import { FordHealthAlertsCard } from "../FordHealthAlertsCard";
import { InServiceDateRow } from "../InServiceDateRow";
import { WarrantyClaimCard } from "../WarrantyClaimCard";
import { FirmwareTrackerCard } from "../FirmwareTrackerCard";
import { ChargingLedgerCard } from "../ChargingLedgerCard";
import { BatteryHistoryCard } from "../BatteryHistoryCard";
import { RecallCheckCard } from "../RecallCheckCard";
import { WarrantyForecastCard } from "../WarrantyForecastCard";
import { FastChargeFitnessCard } from "../FastChargeFitnessCard";
import { ChargeDisciplineCard } from "../ChargeDisciplineCard";
import { TsbMatchCard } from "../TsbMatchCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const kebab = (s) =>
  String(s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/**
 * CareView — Pillar 3 + Pillar 4.
 *
 * Two stacked sections:
 *   • Financial Intelligence — battery value, resale premium, warranty
 *     exposure, replacement cost, depreciation, insurance risk
 *   • Ownership Intelligence — service timeline, recall check hook,
 *     firmware timeline, warranty assistant
 *
 * A "Legacy tools" link at the bottom keeps the old MoreView tools
 * (Cell health map, Warranty countdown, Score lookup, Plans & pricing,
 *  SMS Alerts, Continuity, About) reachable while we transition.
 */
export const CareView = ({ sub, setSub, stateCode }) => {
  const { vehicle } = useVehicle();
  const climate = useMemo(() => getClimate(stateCode), [stateCode]);

  // Hooks MUST come before any conditional return — the sub-view guard
  // lives below.
  // Demo/preview vehicles carry a masked VIN (bullet chars) — never send
  // it to the service-log API (guaranteed 400).
  const rawVin = vehicle?.vin || null;
  const vin = rawVin && /^[A-HJ-NPR-Z0-9]{11,17}$/i.test(rawVin) ? rawVin : null;
  const [serviceLog, setServiceLog] = useState({});   // { service_key: entry }

  const refreshLog = useCallback(async () => {
    if (!vin || !API_BASE) return;
    try {
      const r = await fetch(`${API_BASE}/api/service_log/${encodeURIComponent(vin)}`);
      if (!r.ok) return;
      const data = await r.json();
      const map = {};
      (data.entries || []).forEach((e) => { map[e.service_key] = e; });
      setServiceLog(map);
    } catch {
      /* offline — keep empty */
    }
  }, [vin]);

  useEffect(() => { refreshLog(); }, [refreshLog]);

  // When a sub-view is active, defer to MoreView's routing to keep the
  // Cell health / Alerts / Plans / About screens working unchanged.
  if (sub) return <MoreView sub={sub} setSub={setSub} stateCode={stateCode} />;

  // Live-gated financial + ownership calculations. Fall back to empty
  // states when we don't have live data yet.
  const isLive = !!vehicle?.live;
  const fi = isLive ? financialSnapshot(vehicle) : null;
  const timeline = serviceTimeline(vehicle, serviceLog);
  const risk = warrantyRisk(vehicle);
  const soh = capacityRetention(vehicle);

  const saveEntry = async (service_key, service_date, note) => {
    if (!vin || !API_BASE) return;
    try {
      const r = await fetch(`${API_BASE}/api/service_log/${encodeURIComponent(vin)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_key,
          service_date,
          odometer_mi: vehicle?.odometer || null,
          note: note || null,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        const map = {};
        (data.entries || []).forEach((e) => { map[e.service_key] = e; });
        setServiceLog(map);
      }
    } catch {
      /* fail silently — offline */
    }
  };

  const clearEntry = async (service_key) => {
    if (!vin || !API_BASE) return;
    try {
      const r = await fetch(
        `${API_BASE}/api/service_log/${encodeURIComponent(vin)}/${encodeURIComponent(service_key)}`,
        { method: "DELETE" },
      );
      if (r.ok) {
        const data = await r.json();
        const map = {};
        (data.entries || []).forEach((e) => { map[e.service_key] = e; });
        setServiceLog(map);
      }
    } catch {
      /* offline */
    }
  };

  return (
    <div className="space-y-6 pb-4" data-testid="mobile-app-view-care">
      {/* ==================== Financial Intelligence ==================== */}
      <CollapseSection
        id="care-fi"
        eyebrow="Financial Intelligence (FI)"
        title="Your battery in dollars."
        kicker="Battery value is now the single largest depreciation lever on an EV — track it like an asset."
      >

        {!isLive ? (
          <ConnectPrompt msg="Sign in with Tesla to unlock live battery value + resale numbers." />
        ) : (
          <>
            {/* Battery value hero */}
            <div
              className="rounded-2xl border p-4"
              style={{
                borderColor: "rgba(57,255,20,0.28)",
                background: "linear-gradient(135deg, rgba(57,255,20,0.06), rgba(0,229,255,0.04))",
              }}
              data-testid="fi-hero"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center border"
                  style={{ background: "rgba(57,255,20,0.14)", borderColor: "rgba(57,255,20,0.45)" }}
                >
                  <CircleDollarSign className="w-4 h-4" style={{ color: COLOR.green }} strokeWidth={2.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
                    Battery value
                  </div>
                  <div className="font-display text-2xl text-white font-semibold leading-none mt-1">
                    ${fi.batteryValueNow.toLocaleString()}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                    of vehicle
                  </div>
                  <div className="font-display text-lg text-white font-semibold leading-none mt-1">
                    {fi.batteryContributionPct}%
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/5">
                <MiniLine label="Estimated vehicle" value={`$${fi.vehicleValueEst.toLocaleString()}`} color={COLOR.cyan} />
                <MiniLine label="Resale premium" value={`+$${fi.resalePremium.toLocaleString()}`} color={COLOR.green} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatChipCard
                label="Warranty exposure"
                value={fi.warrantyExposure}
                trend={`risk ${risk}/100`}
                color={fi.warrantyExposure === "Low" ? COLOR.green : fi.warrantyExposure === "Moderate" ? COLOR.amber : COLOR.red}
                testid="fi-warranty-exposure"
              />
              <StatChipCard
                label="Insurance risk"
                value={fi.insuranceRisk}
                trend="pack-driven"
                color={fi.insuranceRisk === "Low" ? COLOR.green : COLOR.amber}
                testid="fi-insurance-risk"
              />
              <StatChipCard
                label="Replacement cost"
                value={`$${fi.replacementCost.toLocaleString()}`}
                trend="pack + labor"
                color={COLOR.amber}
                testid="fi-replacement-cost"
              />
              <StatChipCard
                label="Depreciation · next 12mo"
                value={`$${fi.depreciationNextYear.toLocaleString()}`}
                trend="projected"
                color={COLOR.red}
                testid="fi-depreciation"
              />
            </div>
          </>
        )}
      </CollapseSection>

      {/* ==================== Warranty Assistant ==================== */}
      <CollapseSection
        id="care-warranty"
        eyebrow="Warranty Assistant"
        title="Approaching thresholds."
        kicker="EVIQ never claims a warranty qualification — we surface when a professional evaluation is worth booking."
        defaultOpen
      >

        {!isLive ? (
          <ConnectPrompt msg="Sign in with Tesla to monitor your warranty window." />
        ) : (
          <>
            {/* Auto-drafted claim assistant — shows a healthy chip when
                the pack is well inside the window and switches to a
                red action card when the pack is on track to cross the
                floor within ~6 months. */}
            <WarrantyClaimCard
              vehicle={vehicle}
              buildSnapshot={(v) => {
                const risk2 = batteryRiskScore(v, climate);
                const forecast = futureHealthForecast(v);
                const value = resaleBatteryValue(v);
                const thermal = thermalStressIndex(v, climate);
                const charging = chargingQualityScore(v);
                const calib = calibrationConfidence(v);
                const score = eviqScore(v, climate);
                return {
                  vin: v.vin || "UNKNOWN",
                  year: v.year || new Date().getFullYear() - 3,
                  model: v.model || "Model 3",
                  trim: v.trim || null,
                  soh_pct: v.sohCurrent ?? 92,
                  delta_30d_pct: v.delta30d ?? -0.2,
                  epa_when_new_mi: v.epaRange || 300,
                  pack_kwh: v.packKwh || 75,
                  warranty_years: warrantyFor(v).years,
                  warranty_miles: warrantyFor(v).miles,
                  warranty_threshold_pct: warrantyFor(v).threshold,
                  risk_score: risk2.value,
                  risk_band: risk2.band,
                  thermal_stress_index: thermal,
                  charging_quality_score: charging,
                  calibration_confidence_pct: calib,
                  fleet_rank_pct: 50,
                  battery_value_usd: value.now,
                  forecast_3yr_soh: forecast.y3,
                  forecast_8yr_soh: forecast.y8,
                  eviq_score: score.total,
                  eviq_score_grade: score.grade,
                };
              }}
            />
            <MeterBarCard
              label="Warranty risk"
              value={risk}
              suffix="/100"
              desc={risk < 25 ? "Well inside the warranty window — nothing to do." : risk < 55 ? "Track this quarterly. Trend is not urgent." : `Consider a ${serviceLabel(vehicle)} diagnostic pull in the next 30–60 days.`}
              inverse
              testid="care-warranty-meter"
            />
            <WarrantyForecastCard vehicle={vehicle} />
            <FactorListCard
              header="Service readiness checklist"
              testid="care-readiness-checklist"
              items={[
                { icon: ShieldCheck, color: COLOR.green, text: `Current SoH ${soh}%`, hint: `Warranty floor ${vehicle.warranty?.threshold ?? 70}%` },
                { icon: Battery,     color: COLOR.cyan,  text: `Warranty coverage · ${warrantyFor(vehicle).years} yr / ${(warrantyFor(vehicle).miles / 1000).toFixed(0)}k mi`, hint: "Check your original bill of sale for exact start date." },
                { icon: FileWarning, color: COLOR.amber, text: `If you approach threshold: book ${serviceLabel(vehicle)} diagnostic`, hint: "They can pull the BMS log & confirm — we only surface the signal." },
              ]}
            />
          </>
        )}
      </CollapseSection>

      {/* ==================== Ownership Timeline ==================== */}
      <CollapseSection
        id="care-ownership"
        eyebrow="Ownership Intelligence"
        title="Service timeline."
        kicker={`Estimated intervals from ${vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"}'s own service manual — cross-checked with your live odometer.`}
      >

        {!isLive ? (
          <ConnectPrompt msg="Sign in with Tesla to load your odometer & timeline." />
        ) : (
          <div className="space-y-2" data-testid="care-timeline">
            {timeline.map((t) => {
              const key = kebab(t.label);
              return (
                <ServiceRow
                  key={t.label}
                  item={t}
                  serviceKey={key}
                  lastEntry={serviceLog[key] || null}
                  onSave={(date, note) => saveEntry(key, date, note)}
                  onClear={() => clearEntry(key)}
                />
              );
            })}
          </div>
        )}

        {/* Recall + firmware + charging ledger + battery history — real live data now */}
        {isLive && (
          <>
            <InServiceDateRow />
            <FordHealthAlertsCard vehicle={vehicle} />
            <BatteryHistoryCard vehicle={vehicle} />
            <FirmwareTrackerCard vehicle={vehicle} />
            <ChargingLedgerCard vehicle={vehicle} />
            <FastChargeFitnessCard vehicle={vehicle} />
            <ChargeDisciplineCard vehicle={vehicle} />
          </>
        )}
        <div className="grid grid-cols-1 pt-1">
          <RecallCheckCard vehicle={vehicle} />
        </div>
        <TsbMatchCard vehicle={vehicle} />
      </CollapseSection>

      {/* ==================== Plans & pricing ==================== */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setSub("pricing")}
          className="w-full flex items-center gap-3 rounded-xl border px-3 py-3 hover:bg-white/[0.04] transition-colors"
          style={{
            borderColor: "rgba(57,255,20,0.30)",
            background: "rgba(57,255,20,0.04)",
          }}
          data-testid="care-open-pricing"
        >
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center border"
            style={{ background: "rgba(57,255,20,0.14)", borderColor: "rgba(57,255,20,0.45)" }}
          >
            <Sparkles className="w-4 h-4" style={{ color: COLOR.green }} strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[13px] text-white font-semibold leading-tight">
              Plans &amp; pricing
            </div>
            <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
              Compare Basic · Premium · Lifetime — see what each unlocks
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </button>
      </div>

      {/* ==================== My Forest ==================== */}
      <button
        type="button"
        onClick={() => setSub("forest")}
        className="w-full flex items-center gap-3 rounded-xl border px-3 py-3 hover:bg-white/[0.04] transition-colors"
        style={{ borderColor: "rgba(57,255,20,0.22)", background: "rgba(0,0,0,0.24)" }}
        data-testid="care-open-forest"
      >
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border"
          style={{ background: "rgba(57,255,20,0.14)", borderColor: "rgba(57,255,20,0.45)" }}
        >
          <Sprout className="w-4 h-4" style={{ color: COLOR.green }} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[13px] text-white font-semibold leading-tight">My Forest</div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            Trees planted in your name — subscription, health &amp; referral rewards
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </button>

      {/* ==================== More tools (legacy MoreView list) ==================== */}
      <button
        type="button"
        onClick={() => setSub("tools")}
        className="w-full flex items-center gap-3 rounded-xl border px-3 py-3 hover:bg-white/[0.04] transition-colors"
        style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.24)" }}
        data-testid="care-open-tools"
      >
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border"
          style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.14)" }}
        >
          <Grid3X3 className="w-4 h-4 text-zinc-300" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="text-[13px] text-white font-semibold leading-tight">More tools</div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            Cell map · warranty countdown · VIN lookup · alerts &amp; language
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-500" />
      </button>
    </div>
  );
};

const MiniLine = ({ label, value, color }) => (
  <div>
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
      {label}
    </div>
    <div className="font-display text-sm font-semibold leading-none" style={{ color }}>
      {value}
    </div>
  </div>
);

const ConnectPrompt = ({ msg }) => (
  <div
    className="rounded-xl border p-4 flex items-center gap-3"
    style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
  >
    <AlertCircle className="w-4 h-4 text-zinc-400" strokeWidth={2.2} />
    <div className="text-[12px] text-zinc-300 leading-relaxed">{msg}</div>
  </div>
);

const ServiceRow = ({ item, serviceKey, lastEntry, onSave, onClear }) => {
  const urgency = item.dueInMi < 500 ? "now" : item.dueInMi < 3000 ? "soon" : "later";
  const urgencyColor = urgency === "now" ? COLOR.red : urgency === "soon" ? COLOR.amber : COLOR.green;
  const CategoryIcon = item.cat === "battery" ? Battery : item.cat === "safety" ? ShieldCheck : Wrench;

  const today = new Date().toISOString().slice(0, 10);
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(lastEntry?.service_date || today);
  const [note, setNote] = useState(lastEntry?.note || "");

  const rowTestid = `care-timeline-${serviceKey}`;

  return (
    <div
      className="rounded-xl border p-3"
      style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
      data-testid={rowTestid}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
          style={{ background: `${urgencyColor}12`, borderColor: `${urgencyColor}44` }}
        >
          <CategoryIcon className="w-4 h-4" style={{ color: urgencyColor }} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-white font-semibold leading-tight">{item.label}</div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            Due at {item.dueAtMi.toLocaleString()} mi · {item.dueInMi.toLocaleString()} mi to go
            {item.shifted && (
              <span
                className="ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-[1px] font-mono text-[9px] uppercase tracking-wider"
                style={{ background: `${COLOR.green}18`, color: COLOR.green }}
                data-testid={`${rowTestid}-shifted`}
                title="Next-due milestone auto-shifted from your logged service date"
              >
                <Sparkles className="w-2 h-2" strokeWidth={2.6} />
                Auto-shifted
              </span>
            )}
          </div>
        </div>
        <span
          className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ background: `${urgencyColor}14`, color: urgencyColor }}
        >
          {urgency}
        </span>
      </div>

      {/* Last-done chip (owner-recorded service) */}
      {lastEntry && !editing && (
        <div
          className="mt-2 flex items-center gap-2 pl-11"
          data-testid={`${rowTestid}-last-done`}
        >
          <Check className="w-3 h-3 shrink-0" style={{ color: COLOR.green }} strokeWidth={2.6} />
          <div className="text-[11px] text-zinc-300 leading-snug flex-1 min-w-0 truncate">
            Last done <span className="font-mono text-white">{lastEntry.service_date}</span>
            {lastEntry.odometer_mi != null && (
              <span className="text-zinc-500 font-mono">
                {" "}· {lastEntry.odometer_mi.toLocaleString()} mi
              </span>
            )}
            {lastEntry.note && (
              <span className="text-zinc-500 italic"> · {lastEntry.note}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="font-mono text-[9px] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
            data-testid={`${rowTestid}-edit`}
          >
            Edit
          </button>
        </div>
      )}

      {/* Log form: show either an "Add" button or the editor */}
      {!editing && !lastEntry && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-2 ml-11 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white hover:bg-white/[0.04] transition-colors"
          style={{ borderColor: "rgba(255,255,255,0.15)" }}
          data-testid={`${rowTestid}-log-btn`}
        >
          <Calendar className="w-3 h-3" strokeWidth={2.2} />
          Log last service
        </button>
      )}

      {editing && (
        <div
          className="mt-2 ml-11 rounded-lg border p-2.5 space-y-2"
          style={{ borderColor: "rgba(0,229,255,0.25)", background: "rgba(0,229,255,0.04)" }}
          data-testid={`${rowTestid}-editor`}
        >
          <label className="block">
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Service date
            </span>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              data-testid={`${rowTestid}-date-input`}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/60 px-2 py-1.5 text-[12px] text-white focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Note <span className="text-zinc-600 normal-case font-sans">(shop, part #, anything)</span>
            </span>
            <input
              type="text"
              value={note}
              maxLength={200}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Discount Tire · rotation only"
              data-testid={`${rowTestid}-note-input`}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/60 px-2 py-1.5 text-[12px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#00E5FF]/60 focus:ring-1 focus:ring-[#00E5FF]/30"
            />
          </label>
          <div className="flex items-center gap-2 justify-end">
            {lastEntry && (
              <button
                type="button"
                onClick={async () => {
                  await onClear();
                  setEditing(false);
                }}
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-[#FF6B57] hover:border-[#FF6B57]/40 transition-colors"
                data-testid={`${rowTestid}-delete`}
              >
                <Trash2 className="w-3 h-3" strokeWidth={2.2} />
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white transition-colors"
              data-testid={`${rowTestid}-cancel`}
            >
              <XIcon className="w-3 h-3" strokeWidth={2.2} />
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                await onSave(date, note.trim() || null);
                setEditing(false);
              }}
              disabled={!date}
              className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${rowTestid}-save`}
            >
              <Check className="w-3 h-3" strokeWidth={2.4} />
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
